import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, MoreHorizontal, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { apiFetch, ApiError } from "@/api/client";
import type { MailboxResponse } from "@/api/schemas";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { isSuperadmin } from "@/lib/roles";
import { useAuth } from "@/stores/auth";

const searchSchema = z.object({
  // Accept several truthy forms but do NOT transform: if we transformed to
  // boolean, TanStack Router would serialize `false` back into the URL on the
  // next navigation and the subsequent parse would fail (`false` isn't in the
  // union). Components check `connected !== undefined` to decide the banner.
  connected: z
    .union([z.literal("1"), z.literal(1), z.literal(true), z.literal("true")])
    .optional(),
  mailbox_id: z.string().uuid().optional(),
  error: z.string().optional(),
  detail: z.string().optional(),
});

function MailboxesPage() {
  const { connected, error, detail } = Route.useSearch();
  const navigate = useNavigate();
  const role = useAuth((s) => s.user?.role);
  const superadmin = isSuperadmin(role);
  const [connectLoading, setConnectLoading] = useState(false);
  const [orgPickerOpen, setOrgPickerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MailboxResponse | null>(null);

  const {
    data: mailboxes,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["mailboxes"],
    queryFn: () => apiFetch<MailboxResponse[]>("/api/mailboxes"),
    refetchInterval: 15_000,
  });

  async function startConnect(orgId?: string) {
    setConnectLoading(true);
    try {
      const res = await apiFetch<{ authorization_url: string }>(
        "/api/mailboxes/connect",
        {
          method: "POST",
          json: orgId ? { organization_id: orgId } : {},
        },
      );
      window.location.href = res.authorization_url;
    } catch (e) {
      setConnectLoading(false);
      toast.error(e instanceof ApiError ? e.message : "Could not start OAuth flow");
    }
  }

  function onConnect() {
    if (superadmin) {
      setOrgPickerOpen(true);
    } else {
      void startConnect();
    }
  }

  function dismissBanner() {
    navigate({ to: "/settings/mailboxes", search: {}, replace: true });
    void refetch();
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium tracking-tight">Mailboxes</h1>
          <p className="mt-1 text-sm text-text-muted">
            Connect Gmail inboxes so customer email arrives as tickets.
          </p>
        </div>
        <Button onClick={onConnect} disabled={connectLoading}>
          {connectLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Connect Gmail
        </Button>
      </div>

      {connected !== undefined && (
        <div className="mb-4 flex items-start justify-between rounded border border-success-subtle bg-success-subtle px-4 py-3 text-sm text-success">
          <div>
            <div className="font-medium">Mailbox connected</div>
            <div className="mt-0.5 text-xs text-text-muted">
              Gmail is linked. Ingestion will start shortly.
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={dismissBanner}>
            Dismiss
          </Button>
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-start justify-between rounded border border-danger-subtle bg-danger-subtle px-4 py-3 text-sm text-danger">
          <div>
            <div className="font-medium">Could not connect</div>
            <div className="mt-0.5 font-mono text-xs">{error}</div>
            {detail && <div className="mt-1 text-xs text-text-muted">{detail}</div>}
          </div>
          <Button size="sm" variant="ghost" onClick={dismissBanner}>
            Dismiss
          </Button>
        </div>
      )}

      <div className="rounded-lg border border-border bg-surface">
        {isLoading ? (
          <ul className="divide-y divide-border">
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="flex items-start justify-between px-4 py-3">
                <div className="flex items-start gap-3">
                  <Skeleton className="h-8 w-8 rounded" />
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-40" />
                    <Skeleton className="h-3 w-24" />
                    <div className="flex gap-2 pt-0.5">
                      <Skeleton className="h-3 w-12" />
                      <Skeleton className="h-3 w-16" />
                      <Skeleton className="h-3 w-14" />
                    </div>
                  </div>
                </div>
                <Skeleton className="h-3 w-20" />
              </li>
            ))}
          </ul>
        ) : !mailboxes || mailboxes.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-subtle">
              <Mail className="h-4 w-4 text-text-muted" />
            </div>
            <div className="text-sm font-medium">No mailboxes connected</div>
            <div className="mt-1 text-xs text-text-muted">
              Click <span className="font-mono">Connect Gmail</span> to link your first inbox.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {mailboxes.map((m) => (
              <li key={m.id} className="flex items-start justify-between px-4 py-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded bg-surface-subtle">
                    <Mail className="h-4 w-4 text-text-muted" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">
                      {m.display_name || m.email_address}
                    </div>
                    {m.display_name && (
                      <div className="text-xs text-text-muted">{m.email_address}</div>
                    )}
                    <div className="mt-0.5 flex flex-wrap gap-2 text-xs text-text-muted">
                      <span
                        className={
                          m.is_active
                            ? "rounded bg-success-subtle px-1.5 py-0.5 text-success"
                            : "rounded bg-danger-subtle px-1.5 py-0.5 text-danger"
                        }
                      >
                        {m.is_active ? "active" : "inactive"}
                      </span>
                      <span className="rounded bg-surface-subtle px-1.5 py-0.5 font-mono">
                        {m.assignment_mode}
                      </span>
                      <span className="rounded bg-surface-subtle px-1.5 py-0.5 font-mono">
                        {m.messages_count} msg
                      </span>
                      {m.gmail_history_id && (
                        <span
                          className="rounded bg-surface-subtle px-1.5 py-0.5 font-mono"
                          title={`Gmail historyId: ${m.gmail_history_id}`}
                        >
                          h{m.gmail_history_id.slice(-6)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right text-xs text-text-faint">
                    <div>
                      {m.last_sync_at ? (
                        <>Synced {formatAge(m.last_sync_at)}</>
                      ) : (
                        <span className="italic">Never synced</span>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" aria-label="Actions">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onSelect={() =>
                          navigate({
                            to: "/settings/mailboxes/$mailboxId",
                            params: { mailboxId: m.id },
                          })
                        }
                      >
                        Edit settings
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        destructive
                        onSelect={() => setDeleteTarget(m)}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <MailboxDeleteDialog
        mailbox={deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      />
      {superadmin && (
        <ConnectOrgPickerDialog
          open={orgPickerOpen}
          onOpenChange={setOrgPickerOpen}
          onConfirm={(orgId) => {
            setOrgPickerOpen(false);
            void startConnect(orgId);
          }}
        />
      )}
    </div>
  );
}

// ------------------ Connect → org picker (superadmin) ------------------

type OrgRow = { id: string; name: string; slug: string };

function ConnectOrgPickerDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onConfirm: (orgId: string) => void;
}) {
  const [orgId, setOrgId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => apiFetch<OrgRow[]>("/api/organizations"),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Choose target organization</DialogTitle>
          <DialogDescription>
            The new mailbox will be linked to the selected tenant. As a
            superadmin you live in the system org, so we need an explicit
            target.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-text-muted">Loading organizations…</p>
        ) : (
          <Select value={orgId ?? undefined} onValueChange={(v) => setOrgId(v)}>
            <SelectTrigger>
              <SelectValue placeholder="Pick a tenant" />
            </SelectTrigger>
            <SelectContent>
              {(data ?? []).map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name} <span className="text-text-faint">({o.slug})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!orgId} onClick={() => orgId && onConfirm(orgId)}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ Delete dialog ------------------

function MailboxDeleteDialog({
  mailbox,
  onOpenChange,
}: {
  mailbox: MailboxResponse | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => {
      if (!mailbox) throw new Error("no mailbox");
      return apiFetch<void>(`/api/mailboxes/${mailbox.id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      toast.success(`Deleted ${mailbox?.email_address ?? "mailbox"}`);
      void qc.invalidateQueries({ queryKey: ["mailboxes"] });
      onOpenChange(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not delete mailbox");
    },
  });

  if (!mailbox) return null;

  return (
    <Dialog open={!!mailbox} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete mailbox</DialogTitle>
          <DialogDescription>
            Delete <span className="font-medium text-text">{mailbox.email_address}</span>?
            Inbound email will stop being ingested. Existing tickets remain.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ Helpers ------------------

function formatAge(iso: string): string {
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${Math.floor(diffHour / 24)}d ago`;
}

export const Route = createFileRoute("/_auth/settings/mailboxes/")({
  validateSearch: searchSchema,
  component: MailboxesPage,
});
